import React, { useEffect, useState } from "react";
import styled from "styled-components";

import Footer from "src/components/Footer";
import { colors } from "src/styles";
import { WashroomLogsSummaryTable } from "./SummaryTable";
import { FirebaseDataService } from "src/services/Firebase/data";
import SummaryTableHeader from "./SummaryTableHeader";
import { NoTicketsMessage } from "src/components/ListScreen";
import { useIsMobile } from "src/hooks/useIsMobile";
import GenerateWashroomLogsReportModal from "src/components/organisms/GenerateWashroomLogsReportModal";
import { WashroomsLogsReportChecklistValues } from "src/components/organisms/GenerateWashroomLogsReportModal/types";
import { filterActiveLocations } from "src/utils/location";

type WashroomLogsDetailsProps = {
  clientId: string;
  locationId: string;
};

export const WashroomLogsDetails: React.FC<WashroomLogsDetailsProps> = ({ locationId, clientId }) => {
  const [washroomLogsSummaryData, setWashroomLogsSummaryData] = React.useState<any[]>([]);

  const { isMobile } = useIsMobile();

  const [isLoading, setIsLoading] = useState(false);
  const [showCreateReportModal, setShowCreateReportModal] = useState(false);
  const [locationOptions, setLocationOptions] = useState<WashroomsLogsReportChecklistValues>({});

  const getWashroomLogsSummaryData = async () => {
    setIsLoading(true);
    try {
      if (!clientId || !locationId) {
        throw new Error("clientId and locationId must be provided");
      }
      const { data } = await FirebaseDataService.getWashroomLogsSummaryByLocationId(clientId, locationId);

      const flattened: any[] = [];
      Object.keys(data).forEach((dateKey) => {
        const dateObj = data[dateKey];
        Object.keys(dateObj).forEach((washroomKey: string) => {
          flattened.push({
            ...dateObj[washroomKey],
            id: Object.keys(dateObj)[0],
            washroomKey,
            clientName: data[dateKey]?.clientName || "N/A",
            locationName: data[dateKey]?.locationLabel || "N/A",
            washroomLabel: data[dateKey]?.washrooms || "N/A",
          });
        });
      });

      setWashroomLogsSummaryData(flattened);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching washroom logs summary data:", error);
      setWashroomLogsSummaryData([]);
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  const addReportButtonHandler = async () => {
    setShowCreateReportModal(true);
  };

  const createReport = async (locationData: WashroomsLogsReportChecklistValues) => {
    if (!clientId || !locationId) {
      console.error("clientId and locationId must be provided");
      return;
    }
    try {
      setIsLoading(true);
      const locationIds = Object.values(locationData).map((loc) => loc.locationId);
      await FirebaseDataService.createWashroomLogsReport(clientId, locationIds);
      await getWashroomLogsSummaryData();
    } catch (error) {
      console.error("Error creating washroom logs report:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchALLLocationsByClientId = async () => {
    if (!clientId) {
      console.error("clientId must be provided");
      return;
    }
    try {
      setIsLoading(true);
      const { data: locations } = await FirebaseDataService.getLocationsByClientId(clientId);
      const checklistValues: WashroomsLogsReportChecklistValues = {};
      if (Array.isArray(locations)) {
        filterActiveLocations(locations).forEach((loc: any) => {
          let washroomsArr: any[] = [];
          if (loc.washrooms && typeof loc.washrooms === "object" && !Array.isArray(loc.washrooms)) {
            washroomsArr = Object.values(loc.washrooms);
          } else if (Array.isArray(loc.washrooms)) {
            washroomsArr = loc.washrooms;
          }
          const label = loc.label || loc.name || loc.id;
          checklistValues[label] = { washrooms: washroomsArr, locationId: loc.id };
        });
      }
      setLocationOptions(checklistValues);
    } catch (error) {
      console.error("Error fetching locations by clientId:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getWashroomLogsSummaryData();
    fetchALLLocationsByClientId();
  }, [clientId, locationId]);

  return (
    <Container>
      <SummaryTableHeader addReportButtonHandler={addReportButtonHandler} isLoading={isLoading} />
      {washroomLogsSummaryData?.length ? (
        <WashroomLogsSummaryTable
          isLoading={isLoading}
          headers={["Report Date", "Client", "Location", "Washroom ID", "Washroom Label", "Percentage Complete"]}
          data={washroomLogsSummaryData}
          onRowClick={() => {}}
        />
      ) : (
        <NoTicketsMessage $isMobile={isMobile}>No cleaning logs report available</NoTicketsMessage>
      )}
      <StickyFooter />
      {showCreateReportModal && (
        <GenerateWashroomLogsReportModal
          defaultValues={locationOptions}
          closeModal={() => setShowCreateReportModal(false)}
          onSubmit={createReport}
          error=""
        />
      )}
    </Container>
  );
};

const Container = styled.div`
  background-color: ${colors.white.default};
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
  padding-bottom: 60px;
`;

const StickyFooter = styled(Footer)`
  position: sticky;
  margin-top: auto;
`;
